// Two things every data table in this app needed, done once rather than in eighteen views.
//
// 1. On a phone, a table in a horizontally-scrolling wrapper is a table you cannot read: the
//    columns that matter are off to the right and you discover them by accident. Below 768 px
//    each row becomes a card — one column, label and value on a line — and the page scrolls
//    only downwards. The labels come from the table's own <th>, read at runtime, so they can
//    never drift from the header the way hand-written data-label attributes do.
//
// 2. A table that renders every row it has turns a tile into an endless page. Tables longer
//    than ten rows get a pager. Tables that already page on the server opt out with
//    `data-paged="server"`, because two pagers on one table is worse than none.
//
// Both are progressive enhancement. Without JavaScript the table is exactly what it was.
const PAGE = 10;

// A header's visible text. Text that exists only for assistive tech (the label on a select-all
// checkbox) is not a column name, and using it would stamp a sentence onto every row.
function headerText(th) {
  const copy = th.cloneNode(true);
  for (const hidden of copy.querySelectorAll('.visually-hidden, .visually-hidden-focusable'))
    hidden.remove();
  return copy.textContent.trim();
}

function labelCells(table) {
  const headers = [...table.querySelectorAll('thead th')].map(headerText);
  if (!headers.length) return;
  for (const row of table.querySelectorAll('tbody tr')) {
    [...row.children].forEach((cell, index) => {
      const label = headers[index];
      // A cell whose header is empty (a checkbox or an actions column) gets no label, so the
      // card does not grow a row of blank text.
      if (label) cell.setAttribute('data-label', label);
      else cell.classList.add('cell-unlabelled');
    });
  }
}

function paginate(table, strings) {
  const body = table.querySelector('tbody');
  if (!body) return;
  // Rows that belong to the row above them (an inline editor opened by a pencil) travel with
  // it rather than counting as a row of their own.
  const rows = [...body.children].filter((r) => !r.classList.contains('is-subrow'));
  if (rows.length <= PAGE) return;

  const pager = document.createElement('div');
  pager.className = 'table-pager';
  pager.innerHTML =
    '<span class="table-pager-count" aria-live="polite"></span>' +
    '<span class="btn-group btn-group-sm">' +
    '<button class="btn btn-outline-secondary" type="button" data-page="prev"></button>' +
    '<button class="btn btn-outline-secondary" type="button" data-page="next"></button>' +
    '</span>';
  const count = pager.querySelector('.table-pager-count');
  const prev = pager.querySelector('[data-page="prev"]');
  const next = pager.querySelector('[data-page="next"]');
  prev.textContent = strings.prev || 'Previous';
  next.textContent = strings.next || 'Next';

  let page = 0;
  const pages = Math.ceil(rows.length / PAGE);
  function show() {
    const first = page * PAGE;
    rows.forEach((row, index) => {
      const on = index >= first && index < first + PAGE;
      row.hidden = !on;
      // An inline editor hides with the row it belongs to.
      let sub = row.nextElementSibling;
      while (sub && sub.classList.contains('is-subrow')) {
        sub.hidden = !on;
        sub = sub.nextElementSibling;
      }
    });
    count.textContent = (strings.showing || '{{from}}–{{to}} of {{total}}')
      .replace('{{from}}', String(first + 1))
      .replace('{{to}}', String(Math.min(first + PAGE, rows.length)))
      .replace('{{total}}', String(rows.length));
    prev.disabled = page === 0;
    next.disabled = page >= pages - 1;
  }
  prev.addEventListener('click', () => {
    page = Math.max(0, page - 1);
    show();
  });
  next.addEventListener('click', () => {
    page = Math.min(pages - 1, page + 1);
    show();
  });
  (table.closest('.table-responsive') || table).after(pager);
  show();
}

export function initTableCards(root = document) {
  const strings = (() => {
    try {
      return JSON.parse(root.getElementById?.('table-strings')?.textContent || '{}');
    } catch {
      return {};
    }
  })();
  for (const table of root.querySelectorAll('table')) {
    if (table.dataset.cards === 'off') continue;
    table.classList.add('table-cards');
    labelCells(table);
    if (table.dataset.paged !== 'server') paginate(table, strings);
  }
}
